import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  arrayUnion,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

export function useConversations(uid) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "conversations"),
      where("participants", "array-contains", uid),
      orderBy("lastMessageAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const convos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setConversations(convos);
        setLoading(false);
      },
      (err) => {
        console.error("Conversations listener error:", err);
        setLoading(false);
      }
    );

    return unsub;
  }, [uid]);

  // Create a new conversation (1:1 or group).
  // creatorUid is required for the create rule; groupName is optional (null for unnamed).
  const createConversation = async (
    participantUids,
    participantLanguages,
    { creatorUid, groupName = null } = {}
  ) => {
    const type = participantUids.length > 2 ? "group" : "dm";
    const convRef = await addDoc(collection(db, "conversations"), {
      participants: participantUids,
      participantLanguages, // { uid: "en"|"es", ... }
      type,
      groupName: type === "group" ? (groupName || null) : null,
      createdBy: creatorUid,
      createdAt: serverTimestamp(),
      lastMessage: "",
      lastMessageAt: serverTimestamp(),
      lastMessageSenderUid: null,
    });
    return convRef.id;
  };

  // Add participants to an existing conversation
  const addParticipants = async (conversationId, newUsers) => {
    // newUsers: [{ uid, preferredLanguage }, ...]
    const convRef = doc(db, "conversations", conversationId);
    const newUids = newUsers.map((u) => u.uid);
    const langUpdates = {};
    newUsers.forEach((u) => {
      langUpdates[`participantLanguages.${u.uid}`] = u.preferredLanguage || "en";
    });

    await updateDoc(convRef, {
      participants: arrayUnion(...newUids),
      ...langUpdates,
    });
  };

  const renameGroup = async (conversationId, newName) => {
    const trimmed = (newName || "").trim();
    if (!trimmed) throw new Error("Group name cannot be empty");
    await updateDoc(doc(db, "conversations", conversationId), {
      groupName: trimmed,
    });
  };

  return { conversations, loading, createConversation, addParticipants, renameGroup };
}
